import { forwardRef } from 'react';
import './Button.css';

export const BUTTON_VARIANTS = ['primary', 'secondary', 'ghost', 'outline', 'danger'];
export const BUTTON_SIZES = ['sm', 'md'];

/**
 * Bouton du design system (§3.7).
 *  - variant : 'primary' (aplat violet), 'secondary' (aplat orange), 'ghost' (sans fond ni
 *    bordure), 'outline' (bordure, défaut), 'danger' (aplat rouge, action destructrice) ;
 *    tone 'danger' sur ghost ou outline : action destructrice discrète (corbeille d'une
 *    liste, réessayer après une erreur) ;
 *  - size : 'sm' (32 px) ou 'md' (36 px, défaut), 44 px au toucher (P5) ;
 *  - icon : composant d'icône avant le contenu ; iconOnly : bouton carré, `label` devient
 *    son nom accessible (aria-label) et son infobulle ;
 *  - loading : action en cours. Spinner à la place du contenu, gardé dans le flux (largeur
 *    figée, nom accessible inchangé), aria-busy ; le bouton garde le focus (aria-disabled,
 *    jamais disabled) et ignore les clics ;
 *  - as : 'a' pour un lien présenté en bouton (téléchargement, nouvel onglet).
 * Les attributs ARIA, le titre et les gestionnaires sont transmis tels quels : les noms
 * accessibles et les rôles des boutons migrés ne changent pas.
 */
export const Button = forwardRef(function Button(
  {
    variant = 'outline',
    size = 'md',
    tone = null,
    icon: Icon = null,
    iconSize = null,
    iconOnly = false,
    label = null,
    loading = false,
    as: Tag = 'button',
    type,
    disabled,
    className = '',
    title,
    onClick,
    children,
    'aria-label': ariaLabel,
    'aria-disabled': ariaDisabled,
    ...rest
  },
  ref
) {
  const isButton = Tag === 'button';
  const classes = [
    'ds-button',
    `ds-button--${BUTTON_VARIANTS.includes(variant) ? variant : 'outline'}`,
    `ds-button--${BUTTON_SIZES.includes(size) ? size : 'md'}`,
    tone === 'danger' ? 'ds-button--tone-danger' : '',
    iconOnly ? 'ds-button--icon' : '',
    loading ? 'is-loading' : '',
    className,
  ].filter(Boolean).join(' ');

  const handleClick = (event) => {
    if (loading) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  };

  return (
    <Tag
      ref={ref}
      type={isButton ? (type ?? 'button') : undefined}
      className={classes}
      disabled={isButton ? disabled : undefined}
      aria-disabled={loading ? 'true' : ariaDisabled}
      aria-busy={loading ? 'true' : undefined}
      aria-label={ariaLabel ?? (iconOnly && label ? label : undefined)}
      title={title ?? (iconOnly && label ? label : undefined)}
      onClick={handleClick}
      {...rest}
    >
      <span className="ds-button__content">
        {Icon && <Icon size={iconSize ?? (size === 'sm' ? 14 : 16)} />}
        {children ?? (iconOnly ? null : label)}
      </span>
      {loading && <span className="ds-button__spinner" aria-hidden="true" />}
    </Tag>
  );
});
